import React, { useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Spin, Typography } from 'antd';
import { CopyOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiConfig } from '../../authConfig';
import { useApiToken } from '../../hooks/useApiToken';
import { useResource } from '../../hooks/useResource';
import { listApiKeyScopes } from '../../utils/integrationsApi';
import {
    buildCurl, filterOperations, groupByTag, listOperations, paginationParams, schemaRows, scopeUsage, typeLabel,
} from '../../utils/openApiDocs';
import { copyText } from './copyText';
import '../../styles/integrations.css';

const { Text, Paragraph, Title } = Typography;

export const openApiBaseUrl = () => `${apiConfig.baseUrl}/api/v1/open/v1`;
export const openApiSpecUrl = () => `${openApiBaseUrl()}/openapi.json`;

/** The spec is public: fetched without credentials, never through the authenticated envelope helper. */
async function fetchSpec(url) {
    let res;
    try {
        // Deliberately plain: no credentials and no custom headers, so this stays a simple cross-origin GET.
        res = await fetch(url);
    } catch {
        const err = new Error(
            'The documentation request never got a response. This is a network or cross-origin (CORS) problem between this site and the API, '
            + 'not a missing specification. Check your connection, and that the API allows this site to read openapi.json.',
        );
        err.kind = 'network';
        throw err;
    }
    if (res.status === 404) {
        const err = new Error('The Open API specification is not available on this server yet (404). The Open API may not be deployed in this environment.');
        err.kind = 'missing';
        throw err;
    }
    if (!res.ok) {
        const err = new Error(`The specification could not be loaded (HTTP ${res.status}).`);
        err.kind = 'http';
        throw err;
    }
    try {
        const spec = await res.json();
        if (!spec || typeof spec !== 'object' || !spec.paths) throw new Error('bad');
        return spec;
    } catch {
        const err = new Error('The server answered, but not with a valid OpenAPI document.');
        err.kind = 'invalid';
        throw err;
    }
}

const CodeBlock = ({ text, label }) => (
    <div style={{ position: 'relative' }}>
        <code className="int-code" aria-label={label}>{text}</code>
        <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(text)} style={{ marginTop: 6 }}>Copy</Button>
    </div>
);

const SchemaTable = ({ spec, schema }) => {
    const rows = useMemo(() => schemaRows(spec, schema), [spec, schema]);
    if (!rows.length) return <Text type="secondary">No fields.</Text>;
    return (
        <table className="int-doc-table">
            <thead><tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.name}><td><code>{r.name}</code></td><td>{r.type}</td><td>{r.required ? 'yes' : 'no'}</td><td>{r.description}</td></tr>
                ))}
            </tbody>
        </table>
    );
};

const Operation = ({ spec, op }) => (
    <details className="int-op" id={`op-${op.method}-${op.path}`}>
        <summary>
            <span className={`int-method int-method-${op.method.toLowerCase()}`}>{op.method}</span>
            <code className="int-op-path">{op.path}</code>
            <span className="int-op-summary">{op.summary}</span>
        </summary>
        <div className="int-op-body">
            {op.description && <Paragraph>{op.description}</Paragraph>}
            <Paragraph>
                <Text strong>Required scope: </Text>
                {op.scopes.length ? op.scopes.map((s) => <code key={s} className="int-scope">{s}</code>) : <Text type="secondary">none declared in the spec</Text>}
            </Paragraph>

            {op.parameters.length > 0 && (
                <>
                    <Title level={5}>Parameters</Title>
                    <table className="int-doc-table">
                        <thead><tr><th>Name</th><th>In</th><th>Type</th><th>Required</th><th>Description</th></tr></thead>
                        <tbody>
                            {op.parameters.map((p) => (
                                <tr key={`${p.in}:${p.name}`}>
                                    <td><code>{p.name}</code></td><td>{p.in}</td>
                                    <td>{typeLabel(spec, p.schema || {})}</td>
                                    <td>{p.required ? 'yes' : 'no'}</td><td>{p.description || ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {op.bodySchema && (
                <>
                    <Title level={5}>Request body (application/json)</Title>
                    <SchemaTable spec={spec} schema={op.bodySchema} />
                </>
            )}

            <Title level={5}>Responses</Title>
            {op.responses.length === 0 && <Text type="secondary">No responses declared.</Text>}
            {op.responses.map((r) => (
                <div key={r.status} style={{ marginBottom: 10 }}>
                    <Text strong>{r.status}</Text> <Text type="secondary">{r.description}</Text>
                    {r.headers.length > 0 && <div><Text type="secondary">Headers: {r.headers.join(', ')}</Text></div>}
                    {r.schema && <SchemaTable spec={spec} schema={r.schema} />}
                </div>
            ))}

            <Title level={5}>Example</Title>
            <CodeBlock text={buildCurl(spec, openApiBaseUrl(), op)} label={`curl ${op.method} ${op.path}`} />
        </div>
    </details>
);

/** First error-class response schema in the spec, so the documented error shape is the real one. */
const errorShapeOf = (ops) => {
    for (const op of ops) {
        const r = op.responses.find((x) => /^[45]/.test(x.status) && x.schema);
        if (r) return r.schema;
    }
    return null;
};

const Section = ({ id, title, children }) => (
    <section id={id} style={{ marginBottom: 28 }}>
        <Title level={4}>{title}</Title>
        {children}
    </section>
);

const Docs = ({ spec }) => {
    const getToken = useApiToken();
    const [query, setQuery] = useState('');
    const base = openApiBaseUrl();
    const ops = useMemo(() => listOperations(spec), [spec]);
    const filtered = useMemo(() => filterOperations(ops, query), [ops, query]);
    const groups = useMemo(() => groupByTag(filtered), [filtered]);
    const usage = useMemo(() => scopeUsage(ops), [ops]);
    const pagination = useMemo(() => paginationParams(ops), [ops]);
    const errorShape = useMemo(() => errorShapeOf(ops), [ops]);
    const idempotent = useMemo(() => ops.filter((o) => o.parameters.some((p) => p.in === 'header' && /^idempotency-key$/i.test(p.name))), [ops]);
    // Descriptions come from the live scope catalogue when the caller may read it; the table works without them.
    const scopeInfo = useResource(() => listApiKeyScopes(getToken).catch(() => []), [getToken]);
    const describe = (scope) => (scopeInfo.data || []).find((s) => s.scope === scope)?.description || '';

    return (
        <div>
            <Section id="overview" title={spec.info?.title || 'Overview'}>
                {spec.info?.version && <Paragraph type="secondary">Version {spec.info.version}</Paragraph>}
                {spec.info?.description && <Paragraph>{spec.info.description}</Paragraph>}
                <Paragraph><Text strong>Base URL </Text><Text code copyable>{base}</Text></Paragraph>
                <Paragraph>{ops.length} endpoint{ops.length === 1 ? '' : 's'} across {groupByTag(ops).length} resource group{groupByTag(ops).length === 1 ? '' : 's'}. This page is generated from the live specification, so it always matches what the API accepts.</Paragraph>
            </Section>

            <Section id="authentication" title="Authentication">
                <Paragraph>
                    Send your key in the <code>Authorization</code> header, or in the <code>X-API-Key</code> header:
                </Paragraph>
                <CodeBlock text={`Authorization: Bearer plk_live_YOUR_KEY\n# or\nX-API-Key: plk_live_YOUR_KEY`} label="authentication headers" />
                <ul>
                    <li>A key is shown once, when it is created or rotated. Store it in a secret manager; only its prefix is visible afterwards.</li>
                    <li>Never use a key in a browser or in client-side code, and never put it in a query string or URL: URLs are logged and cached.</li>
                    <li>A key can be limited to an IP allowlist. Requests from any other address are refused, even with a valid key.</li>
                    <li>A key can never approve, publish or sign anything. Those actions always require a signed-in human; the API has no endpoint for them.</li>
                    <li>A key sees only your firm's data.</li>
                </ul>
            </Section>

            <Section id="scopes" title="Scopes">
                {usage.length === 0 ? (
                    <Text type="secondary">The specification declares no scopes.</Text>
                ) : (
                    <table className="int-doc-table">
                        <thead><tr><th>Scope</th><th>Description</th><th>Used by</th></tr></thead>
                        <tbody>
                            {usage.map((u) => (
                                <tr key={u.scope}>
                                    <td><code>{u.scope}</code></td>
                                    <td>{describe(u.scope)}</td>
                                    <td>{u.uses.join(', ')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>

            <Section id="conventions" title="Pagination, limits, errors and retries">
                <Title level={5}>Pagination</Title>
                {pagination.length ? (
                    <table className="int-doc-table">
                        <thead><tr><th>Parameter</th><th>Description</th></tr></thead>
                        <tbody>{pagination.map((p) => <tr key={p.name}><td><code>{p.name}</code></td><td>{p.description}</td></tr>)}</tbody>
                    </table>
                ) : <Paragraph type="secondary">The specification declares no pagination parameters.</Paragraph>}

                <Title level={5}>Rate limits</Title>
                <Paragraph>
                    Requests are limited per key per minute. Responses carry <code>RateLimit-Limit</code>, <code>RateLimit-Remaining</code> and <code>RateLimit-Reset</code> headers;
                    when the limit is exceeded the API answers <code>429</code>, so back off until the reset time before retrying.
                </Paragraph>

                <Title level={5}>Errors</Title>
                {errorShape ? <SchemaTable spec={spec} schema={errorShape} /> : <Paragraph type="secondary">Each endpoint lists its error responses below.</Paragraph>}

                <Title level={5}>Idempotency</Title>
                <Paragraph>
                    Send an <code>Idempotency-Key</code> header with <code>POST</code> requests that create records, such as incidents, so a retried request never creates a duplicate.
                    Use a new unique value for each logical request and reuse it only when retrying that same request.
                </Paragraph>
                {idempotent.length > 0 && <Paragraph type="secondary">Declared by: {idempotent.map((o) => `${o.method} ${o.path}`).join(', ')}</Paragraph>}
            </Section>

            <Section id="endpoints" title="Endpoints">
                <Input
                    aria-label="Search endpoints"
                    allowClear
                    placeholder="Search by path, method, resource or scope"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{ marginBottom: 16, maxWidth: 480 }}
                />
                {ops.length === 0 && <Empty description="The specification lists no endpoints." />}
                {ops.length > 0 && filtered.length === 0 && <Empty description={`No endpoints match "${query}".`} />}
                {groups.map((g) => (
                    <div key={g.tag}>
                        <div className="int-section-title">{g.tag}</div>
                        {g.ops.map((op) => <Operation key={`${op.method} ${op.path}`} spec={spec} op={op} />)}
                    </div>
                ))}
            </Section>
        </div>
    );
};

/** Live API documentation, rendered from GET {base}/openapi.json. */
const ApiDocs = () => {
    const specUrl = openApiSpecUrl();
    const spec = useResource(() => fetchSpec(specUrl), [specUrl]);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                <Title level={5} style={{ margin: 0 }}>API documentation</Title>
                <Button icon={<DownloadOutlined />} href={specUrl} target="_blank" rel="noreferrer">Download OpenAPI JSON</Button>
            </div>
            {(spec.status === 'loading' || spec.status === 'idle') && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
            {spec.status === 'error' && (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load the API documentation"
                    description={spec.error.message}
                    action={<Button size="small" icon={<ReloadOutlined />} onClick={spec.reload}>Retry</Button>}
                />
            )}
            {spec.status === 'ready' && <Docs spec={spec.data} />}
        </div>
    );
};

export default ApiDocs;
